import {snakeCase} from 'lodash';
import type {
  Avatar,
  AiAvatarGenerateScriptRequest,
  AiAvatarGenerateScriptResponse,
  Voice,
} from '@contentlab/api/avatars/types';
import createAuthHeaders from '@contentlab/utils/authHeaders';
import streamSseToBlob from '@contentlab/utils/streamSseToBlob';
import contentStudioInstance from '@/utils/axiosContentStudio';
import mapKeysToCase from '@/utils/mapKeysToCase';
import {getEnv} from '@/config/environment';

const getAvatars = async (): Promise<Avatar[]> => {
  const response = await contentStudioInstance.get<Avatar[]>(
    '/api/v1/ai_avatars/avatars'
  );
  return response.data;
};

const generateScripts = async (
  params: AiAvatarGenerateScriptRequest
): Promise<AiAvatarGenerateScriptResponse> => {
  const snakeCaseParams = mapKeysToCase(params, snakeCase);
  const response = await contentStudioInstance.post(
    '/api/v1/ai_avatars/generate_scripts',
    snakeCaseParams
  );
  return response.data as AiAvatarGenerateScriptResponse;
};

const getVoices = async (): Promise<Voice[]> => {
  const response = await contentStudioInstance.get<Voice[]>(
    '/api/v1/ai_avatars/voices'
  );
  return response.data;
};

export interface AiAvatarTtsRequestBody {
  text: string;
  voiceId: string;
}

export interface TtsStreamResult {
  audioUrl: string;
  mimeType: string;
}

const textToSpeechStream = async (
  {text, voiceId}: AiAvatarTtsRequestBody,
  {
    brandId,
    signal,
    onChunk,
  }: {
    brandId?: string;
    signal?: AbortSignal;
    onChunk?: (bytesReceived: number) => void;
  } = {}
): Promise<TtsStreamResult> => {
  const baseUrl = getEnv().VITE_CONTENT_STUDIO_API_HOST;
  const url = `${baseUrl}/api/v1/ai_avatars/tts/preview`;
  const headers = await createAuthHeaders({
    brandId,
    accept: 'text/event-stream',
    contentType: 'application/json',
  });

  const {audioUrl, mimeType} = await streamSseToBlob({
    url,
    headers,
    body: {text, voice_id: voiceId},
    signal,
    onChunk,
    mapChunk: (json: unknown, eventName: string | null) => {
      if (eventName === 'complete') {
        return {done: true};
      }
      if (eventName === 'error') {
        const errObj = json as Record<string, unknown>;
        const message = (errObj?.error as string) || 'TTS stream error';
        const errorCode = errObj?.code as string;
        throw new Error(`${message} (code: ${errorCode})`);
      }
      const obj = json as Record<string, unknown>;
      const base64 = obj?.audio as string;
      return {base64, mime: 'audio/wav'};
    },
  });
  return {audioUrl, mimeType};
};

const avatarFetchers = {
  getAvatars,
  generateScripts,
  getVoices,
  textToSpeechStream,
};

export default avatarFetchers;
